/* https://brandplatform.smartphoto.com/general/molecules/collection-grid-header/ */
import {
	expressAddClass,
	expressEventListener,
	expressQuerySelector,
	expressRemoveClass,
	hasClass,
} from "../../common/html";
import { compositeDisposable, IDisposable } from "../../utils/disposable";

export interface IElementActivationOptions {
	readonly tooltipText?: string;
	readonly enable?: boolean;
}

export interface IGridHeaderControlDependencies {
	readonly onSort: (sortValue: string) => void;
	readonly onDelete: () => Promise<void>;
	readonly onOrderPrints: (bulkUrl: string, maxAmountOfPhotos: number) => void;
	readonly enableOrderPrintsButton?: (selectedItemsCount: number) => IElementActivationOptions;
	readonly onMoveToAnotherFolder?: () => Promise<void>;
	readonly onImportedPhotosCheckboxChanged?: () => void;
	readonly getNrOfSelectedItems: () => number;
	readonly getNrOfSelectableItems: () => number;
	readonly getNrOfVisuallySelectedItems: () => number;
	readonly selectAllClicked: (isSelectAllActive: boolean) => void;
	readonly allPagesLoaded: () => boolean;
}

export interface IGridHeaderControls {
	readonly dispose: () => void;
	readonly getSortValue: () => string;
	readonly isSelectAllActive: (useSelectedStateNoSelectElement?: boolean) => boolean;
	readonly isSelectAllCssActive: () => boolean;
	readonly setSelectAllState: (showPlusIcon: boolean) => void;
	readonly updateAlbumInfo: (count: number) => void;
	readonly isHideImportedChecked: () => boolean;
	readonly enableSelectAll: () => void;
	readonly disableSelectAll: () => void;
	readonly markSelectAllAsActive: (isActivated: boolean) => void;
	readonly reset: () => void;
}

export function createGridHeaderControls(
	containerEl: HTMLElement,
	gridContainerEl: HTMLElement,
	deps: IGridHeaderControlDependencies,
) {
	const ALL_SELECTED_CLASS = "m-collection-grid-header__btn-select-all--selected";
	const ALL_ACTIVE_CLASS = "m-collection-grid-header__btn-select-all--active";
	const ALL_DISABLED_CLASS = "m-collection-grid-header__btn-select-all--disabled";
	const ICON_DISABLED_CLASS = "a-icon--disabled-state";
	const DELETE_DISABLED_CLASS = "m-collection-grid-header__btn-delete-all--disabled";
	const BUTTON__DISABLED_CLASS = "a-button--disabled";
	const sort = expressQuerySelector<HTMLSelectElement>(containerEl, ".technical-grid-sort-selection", false);
	const selectAllContainer = expressQuerySelector<HTMLElement>(
		containerEl,
		".technical-grid-select-all-container",
		false,
	);
	const selectAllContent = selectAllContainer
		? expressQuerySelector<HTMLElement>(selectAllContainer, ".technical-select-all-content", false)
		: null;
	const selectAllTooltip = selectAllContainer
		? expressQuerySelector(selectAllContainer, ".technical-select-all-tooltip")
		: null;
	const deleteContainer = expressQuerySelector<HTMLElement>(containerEl, ".technical-grid-delete-container", false);
	const deleteEl = deleteContainer
		? expressQuerySelector<HTMLElement>(deleteContainer, ".technical-grid-delete", false)
		: null;
	const deleteContent = deleteContainer
		? expressQuerySelector<HTMLElement>(deleteContainer, ".technical-delete-content", false)
		: null;
	const moveEl = expressQuerySelector<HTMLElement>(containerEl, ".technical-grid-move", false);
	const orderPrintsEl = expressQuerySelector<HTMLElement>(containerEl, ".technical-grid-order-prints", false);
	const albumInfoEl = expressQuerySelector<HTMLElement>(gridContainerEl, ".technical-albuminfo", false);
	const refreshEl = expressQuerySelector<HTMLElement>(containerEl, ".technical-grid-refresh", false);
	const hideImportedEl = expressQuerySelector<HTMLInputElement>(containerEl, "#HideImported", false);
	let disposable: IDisposable;
	const {
		onSort,
		onDelete,
		onOrderPrints,
		onMoveToAnotherFolder,
		enableOrderPrintsButton,
		onImportedPhotosCheckboxChanged,
		getNrOfSelectedItems,
		getNrOfSelectableItems,
		getNrOfVisuallySelectedItems,
		selectAllClicked,
		allPagesLoaded,
	} = deps;

	const callOnSort = () => onSort(sort.value);

	const callOnDelete = () => {
		if (!hasClass(deleteEl, ICON_DISABLED_CLASS) && !hasClass(deleteContainer, DELETE_DISABLED_CLASS))
			return onDelete();
	};

	const isSelectAllCssActive = () => {
		return selectAllContainer?.classList.contains(ALL_SELECTED_CLASS);
	};
	const markSelectAllCssActive = (isActive: boolean) => {
		selectAllContainer?.classList.toggle(ALL_SELECTED_CLASS, isActive);
	};
	const isSelectAllActive = (useSelectedStateNoSelectElement?: boolean) => {
		return (
			(useSelectedStateNoSelectElement && !selectAllContainer) ||
			selectAllContainer?.dataset.isActivated == true.toString()
		);
	};
	const markSelectAllAsActive = (isActivated: boolean) => {
		if (!selectAllContainer) return;

		selectAllContainer.dataset.isActivated = isActivated.toString();
	};

	const enable = (el: HTMLElement, tooltipTextContent?: string) => {
		if (!el) return;
		if (hasClass(el, "a-button")) expressRemoveClass(el, BUTTON__DISABLED_CLASS);
		if (hasClass(el, "a-icon")) expressRemoveClass(el, ICON_DISABLED_CLASS);
		const toolTip = el.nextElementSibling as HTMLElement;
		if (!toolTip || !toolTip.classList.contains("m-tooltip")) return;
		const typography = expressQuerySelector(toolTip, ".a-typography");
		if (!typography) return;
		typography.textContent = tooltipTextContent || toolTip.dataset.selected || "";
	};

	const disable = (el: HTMLElement, tooltipTextContent?: string) => {
		if (!el) return;
		if (hasClass(el, "a-button")) expressAddClass(el, BUTTON__DISABLED_CLASS);
		if (hasClass(el, "a-icon")) expressAddClass(el, ICON_DISABLED_CLASS);
		const toolTip = el.nextElementSibling as HTMLElement;
		if (!toolTip || !toolTip.classList.contains("m-tooltip")) return;
		const typography = expressQuerySelector(toolTip, ".a-typography");
		if (!typography) return;
		typography.textContent = tooltipTextContent || toolTip.dataset.nonselected || "";
	};

	const enableAll = (selectedItemCount: number) => {
		const activationOptions = enableOrderPrintsButton && enableOrderPrintsButton(selectedItemCount);
		activationOptions && !activationOptions.enable
			? disable(orderPrintsEl, activationOptions.tooltipText)
			: enable(orderPrintsEl, activationOptions?.tooltipText);

		if (deleteEl) {
			enable(deleteEl);
			deleteContainer.classList.remove(DELETE_DISABLED_CLASS);
		}

		if (moveEl) enable(moveEl);
	};

	const disableAll = () => {
		if (orderPrintsEl) disable(orderPrintsEl);
		if (deleteEl && (deleteEl.dataset.deleteallNoneselected == "false" || getNrOfSelectableItems() == 0)) {
			disable(deleteEl);
			deleteContainer.classList.add(DELETE_DISABLED_CLASS);
		}
		if (moveEl) disable(moveEl);
	};

	const callOnRefresh = () => {
		if (refreshEl.classList.contains(ICON_DISABLED_CLASS)) return; // To prevent spamming the api
		const timeout = parseInt(refreshEl.getAttribute("data-refresh-timeout"), 10) || 15;
		let angle = 0;

		disable(refreshEl);

		function rotate() {
			angle += 15;
			refreshEl.style.transform = "rotate(" + angle + "deg)";
			if (angle >= 360) return;
			requestAnimationFrame(rotate);
		}

		rotate();

		onSort("");

		setTimeout(() => {
			return enable(refreshEl);
		}, timeout * 1000);
	};

	const selectAllClickedEvent = (e: Event) => {
		const selectAll = !isSelectAllCssActive();

		markSelectAllAsActive(selectAll);
		updateSelectAllClasses();

		selectAllClicked && selectAllClicked(selectAll);
	};

	const updateSelectAllClasses = (reset = false) => {
		if (reset) {
			selectAllContainer?.classList.toggle(ALL_ACTIVE_CLASS, false);
			markSelectAllCssActive(false);
			return;
		}

		const nrOfSelectedItems = getNrOfSelectedItems();
		const nrOfSelectableItems = getNrOfSelectableItems();
		const nrOfVisuallySelectedItems = getNrOfVisuallySelectedItems();
		let enableSelectAll = isSelectAllActive() && nrOfVisuallySelectedItems == nrOfSelectableItems;
		if (!enableSelectAll)
			enableSelectAll =
				!isSelectAllActive() && allPagesLoaded() && nrOfSelectedItems == nrOfSelectableItems && nrOfSelectableItems > 0;

		//Anything selected
		selectAllContainer?.classList.toggle(ALL_ACTIVE_CLASS, nrOfSelectedItems > 0);

		//Everything selected
		markSelectAllCssActive(enableSelectAll);
	};

	const enableSelectAll = () => {
		selectAllContainer && selectAllContainer.classList.remove(ALL_DISABLED_CLASS);
		selectAllTooltip && selectAllTooltip.classList.add("u-hide");
	};

	const disableSelectAll = () => {
		selectAllContainer && selectAllContainer.classList.add(ALL_DISABLED_CLASS);
		selectAllTooltip && selectAllTooltip.classList.remove("u-hide");
	};

	const setSelectAllState = (showPlusIcon: boolean) => {
		const nrOfSelectedItems = getNrOfSelectedItems();
		updateSelectAllClasses();

		if (nrOfSelectedItems != 0) {
			if (selectAllContent) {
				selectAllContent.innerHTML = (selectAllContent.dataset.selected || "").replace(
					"{0}",
					nrOfSelectedItems.toString(),
				);
				if (showPlusIcon) selectAllContent.innerHTML += "+";
			}
			if (deleteContent) {
				if (deleteEl.dataset.deleteallNoneselected == "true" && getNrOfSelectableItems() > 0) {
					enable(deleteEl);
					deleteContainer.classList.remove(DELETE_DISABLED_CLASS);
				}
				deleteContent.innerHTML = (deleteContent.dataset.selected || "").replace("{0}", nrOfSelectedItems.toString());
				if (showPlusIcon) deleteContent.innerHTML += "+";
			}

			enableAll(nrOfSelectedItems);
		} else {
			if (selectAllContent) {
				selectAllContent.innerHTML = selectAllContent.dataset.nonselected;
			}

			if (deleteContent) {
				if (deleteEl.dataset.deleteallNoneselected == "true" && getNrOfSelectableItems() > 0) {
					enable(deleteEl);
					deleteContainer.classList.remove(DELETE_DISABLED_CLASS);
				}
				deleteContent.innerHTML = deleteContent.dataset.nonselected;
			}

			disableAll();
		}
	};

	const reset = () => {
		markSelectAllAsActive(false);
		updateSelectAllClasses(true);

		if (selectAllContent) selectAllContent.innerHTML = selectAllContent.dataset.nonselected;
		if (deleteContent) deleteContent.innerHTML = deleteContent.dataset.nonselected;

		disableAll();
	};

	const callOnOrderPrints = (ev: MouseEvent) => {
		ev.preventDefault();
		if (!hasClass(orderPrintsEl, BUTTON__DISABLED_CLASS)) {
			let bulkUrl = orderPrintsEl.dataset.bulkurl;
			if (sort) {
				switch (sort.value) {
					case "DateTakenAsc":
						bulkUrl += "&sort=DateTaken&order=Ascending";
						break;
					case "DateTakenDesc":
						bulkUrl += "&sort=DateTaken&order=Descending";
						break;
					case "DateUploadedAsc":
						bulkUrl += "&sort=DateAdded&order=Ascending";
						break;
					case "DateUploadedDesc":
						bulkUrl += "&sort=DateAdded&order=Descending";
						break;
					case "NameAsc":
						bulkUrl += "&sort=Name&order=Ascending";
						break;
					case "NameDesc":
						bulkUrl += "&sort=Name&order=Descending";
						break;
				}
			}
			onOrderPrints(bulkUrl, parseInt(orderPrintsEl.dataset.maxamountphotos, 10));
		}
	};

	const onMove = () => {
		if (onMoveToAnotherFolder && !hasClass(moveEl, ICON_DISABLED_CLASS)) onMoveToAnotherFolder();
	};

	const addHandlers = () => {
		disposable = compositeDisposable([]);

		if (sort) disposable.add(expressEventListener(sort, "change", callOnSort));

		if (deleteContainer) disposable.add(expressEventListener(deleteContainer, "click", callOnDelete));

		if (selectAllContainer) disposable.add(expressEventListener(selectAllContainer, "click", selectAllClickedEvent));

		if (orderPrintsEl) disposable.add(expressEventListener(orderPrintsEl, "click", callOnOrderPrints));

		if (moveEl) disposable.add(expressEventListener(moveEl, "click", onMove));

		if (refreshEl) disposable.add(expressEventListener(refreshEl, "click", callOnRefresh));

		if (hideImportedEl && onImportedPhotosCheckboxChanged)
			disposable.add(expressEventListener(hideImportedEl, "click", onImportedPhotosCheckboxChanged));
	};

	const getSortValue = () => {
		return sort ? sort.value : "";
	};

	const updateAlbumInfo = (count: number) => {
		if (albumInfoEl) {
			const albumInfoLabel = albumInfoEl.dataset.infolabel || "";
			if (albumInfoLabel && albumInfoLabel.indexOf("{1}") >= 0)
				albumInfoEl.innerHTML = albumInfoLabel.replace("{1}", count + "");
		}
	};

	const isHideImportedChecked = () => {
		return hideImportedEl?.checked;
	};

	addHandlers();

	if (refreshEl) enable(refreshEl);

	return {
		dispose: () => disposable && disposable.dispose(),
		getSortValue,
		isSelectAllActive,
		isSelectAllCssActive,
		setSelectAllState,
		updateAlbumInfo,
		isHideImportedChecked,
		enableSelectAll,
		disableSelectAll,
		markSelectAllAsActive,
		reset,
	} as IGridHeaderControls;
}
